import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { Box, ActionMenu, ActionList } from "@primer/react";
import {
  TrashIcon,
  CheckIcon,
  KebabHorizontalIcon,
  PencilIcon,
  XIcon,
} from "@primer/octicons-react";
import { hinhThucThanhToanApi } from "../../../api/hinhThucThanhToanApi";
import { mucPhaiThuApi } from "../../../api/mucPhaiThuApi";
import { phanBoPhiApi } from "../../../api/phanBoPhiApi";
import { showNotify } from "../../../helpers/toast";
import { PageBaseStatus } from "../../../models/pageBaseStatus";
import { sf_mucphaithu_select_byhocsinh_rq } from "../../../models/request/thu-phi/sf_mucphaithu_select_byhocsinh_rq";
import { sf_mucphaithu } from "../../../models/response/thu-phi/sf_mucphaithu";
import { ts_hocsinh } from "../../../models/response/hocsinh/ts_hocsinh";
import { sf_hinhthucthanhtoan } from "../../../models/request/hinh-thuc-thanh-toan/sf_hinhthucthanhtoan";
import { RootState } from "../../../state/reducers";
import "../hinh-thuc-thu-phi/style.css";
import "./style.css";
import TextInput from "../../ui/text-input";
import NumberInput from "../../ui/number-input";
import Button from "../../ui/button";
import DataTable, { IColumn } from "../../ui/data-table/DataTable";
import TextWithEllipse from "../../ui/text-with-ellipse";

export type ThuPhiThongTinTongHopProps = {
  sf_mucphaithu: sf_mucphaithu;
  ts_hocsinh: ts_hocsinh;
  sf_phanbophis?: any[];
  dataSource?: any[];
  sf_bienlaithu?: any[];
  onHinhThucClick?: (hinhThuc: any) => void;
  tong_tien_thua?: number;
  tong_tien_da_nop_custom?: number;
  onPhanBoPhiChanged?: () => void;
};

const ThuPhiThongTinTongHop = (props: ThuPhiThongTinTongHopProps) => {
  const { dm_coso_id, nam_hoc } = useSelector((x: RootState) => x.common);
  const [tongTienDinhMuc, setTongTienDinhMuc] = useState<number>(0);
  const [tongTienMienGiam, setTongTienMienGiam] = useState<number>(0);
  const [tongTienPhaiNop, setTongTienPhaiNop] = useState<number>(0);
  const [tongTienConLai, setTongTienConLai] = useState<number>(0);
  const [sf_hinhthucthanhtoans, set_sf_hinhthucthanhtoans] = useState<
    sf_hinhthucthanhtoan[]
  >([]);
  const [selectedHinhThuc, setSelectedHinhThuc] = useState<any>(null);
  const [selected, setSelected] = useState<any>([]);
  const [selectedTienThua, setSelectedTienThua] = useState<any>([]);
  const [status, setStatus] = useState<string>(PageBaseStatus.is_completed);
  const [sf_phanbophis, set_sf_phanbophis] = useState<any[]>([]);
  const [tong_tien_thua, set_tong_tien_thua] = useState<any>(0);
  const [tong_tien_thua_temp, set_tong_tien_thua_temp] = useState<any>(0);
  const [tong_tien_tra_truoc, set_tong_tien_tra_truoc] = useState<any>(0);

  const soTienThuaHienThi =
    (props.tong_tien_thua ?? tong_tien_thua) -
    (props.tong_tien_da_nop_custom ?? 0);

  useEffect(() => {
    if (props.sf_mucphaithu) {
      setTongTienDinhMuc(props.sf_mucphaithu.tong_tien_dinh_muc || 0);
      setTongTienMienGiam(props.sf_mucphaithu.tong_tien_mien_giam || 0);
      setTongTienPhaiNop(props.sf_mucphaithu.tong_tien_phai_nop || 0);
      setTongTienConLai(
        (props.sf_mucphaithu.tong_tien_phai_nop || 0) -
        (props.sf_mucphaithu.tong_tien_da_nop || 0)
      );
    }
  }, [props.sf_mucphaithu]);

  useEffect(() => {
    if (props.dataSource && props.dataSource.length > 0) {
      const realData = props.dataSource.filter((x: any) => !x.isGroupHeader);
      const tongDinhMuc = realData.reduce(
        (sum, item) => sum + (Number(item.so_tien_dinh_muc) || 0),
        0
      );
      const tongPhaiNop = realData.reduce(
        (sum, item) => sum + (Number(item.so_tien_phai_nop) || 0),
        0
      );
      const tongDaNop = realData.reduce(
        (sum, item) => sum + (Number(item.so_tien_da_nop) || 0),
        0
      );

      setTongTienDinhMuc(tongDinhMuc);
      setTongTienPhaiNop(tongPhaiNop);
      setTongTienConLai(tongPhaiNop - tongDaNop);
    }
  }, [props.dataSource]);

  const handleReloadHinhThucThanhToan = useCallback(async () => {
    const res = await hinhThucThanhToanApi.select_all();
    set_sf_hinhthucthanhtoans(
      res.data.filter((x: any) => x.dm_coso_id === dm_coso_id)
    );
  }, [dm_coso_id]);

  useEffect(() => {
    handleReloadHinhThucThanhToan();
  }, [handleReloadHinhThucThanhToan]);

  const handleReloadPhanBoPhi = useCallback(async () => {
    const res = await phanBoPhiApi.select_by_sct(props.sf_mucphaithu.id);
    set_sf_phanbophis(res.data);
  }, [props.sf_mucphaithu.id]);

  const handleReloadTienThuaHocSinh = useCallback(async () => {
    const datasend: sf_mucphaithu_select_byhocsinh_rq = {
      ts_hocsinh_id: props.ts_hocsinh.id,
      nam_hoc: nam_hoc,
    };
    const res = await mucPhaiThuApi.select_tienthua_by_hocsinh(datasend);
    if (res.is_success && res.data && res.data.tong_tien_thua !== undefined) {
      set_tong_tien_thua(res.data.tong_tien_thua);
    }
  }, [props.ts_hocsinh.id, nam_hoc]);

  useEffect(() => {
    handleReloadPhanBoPhi();
    handleReloadTienThuaHocSinh();
  }, [handleReloadPhanBoPhi, handleReloadTienThuaHocSinh]);

  useEffect(() => {
    if (tong_tien_thua && selectedTienThua.so_tien >= 0) {
      set_tong_tien_thua_temp(tong_tien_thua - selectedTienThua.so_tien);
    } else {
      set_tong_tien_thua_temp(tong_tien_thua);
    }
  }, [tong_tien_thua, selectedTienThua]);

  useEffect(() => {
    if (sf_phanbophis.length > 0) {
      const tong_tien_tra_truoc = sf_phanbophis
        .filter((x: any) =>
          x.hinh_thuc_thanh_toan.toLowerCase().includes("trả trước")
        )
        .reduce((sum: number, item: any) => sum + item.so_tien, 0);
      set_tong_tien_tra_truoc(tong_tien_tra_truoc);
    } else {
      set_tong_tien_tra_truoc(0);
    }
  }, [sf_phanbophis]);

  // Cập nhật lại "Còn lại" khi sf_phanbophis thay đổi
  useEffect(() => {
    if (props.sf_mucphaithu) {
      const tongDaNop = sf_phanbophis
        .map((x: any) => x.so_tien)
        .reduce((a: number, b: number) => a + b, 0);
      setTongTienConLai(
        (props.sf_mucphaithu.tong_tien_phai_nop || 0) - tongDaNop
      );
    }
  }, [sf_phanbophis, props.sf_mucphaithu]);

  const onHinhThucClick = (hinhThuc: any) => {
    setSelectedHinhThuc(hinhThuc);
    setSelected([]);
    setSelectedTienThua([]);

    if (hinhThuc && props.sf_mucphaithu) {
      const tongDaNop = sf_phanbophis
        .map((x: any) => x.so_tien)
        .reduce((a: number, b: number) => a + b, 0);
      const conLai = props.sf_mucphaithu.tong_tien_phai_nop - tongDaNop;

      const newData = {
        ...hinhThuc,
        ghi_chu: hinhThuc.phanBoInfo?.ghi_chu || "",
        ma_chuan_chi: hinhThuc.phanBoInfo?.ma_chuan_chi || "",
        serial: hinhThuc.phanBoInfo?.serial || "",
        ngay_thu:
          hinhThuc.phanBoInfo?.ngay_thu ||
          moment().format("YYYY-MM-DD HH:mm:ss"),
        so_tien: hinhThuc.phanBoInfo?.so_tien || conLai,
        sf_mucphaithu_id: props.sf_mucphaithu.id,
        sf_hinhthucthanhtoan_id: hinhThuc.id,
      };
      setSelected(newData);
    }

    if (props.onHinhThucClick) {
      props.onHinhThucClick(hinhThuc);
    }
  };

  const handleSaveChanges = async () => {
    if (selected.is_have_ma_chuan_chi && selected.ma_chuan_chi === "") {
      showNotify({
        message: "Vui lòng điền mã chuẩn chi cho hình thức thu quẹt thẻ",
        type: "warning",
      });
      return false;
    }

    setStatus(PageBaseStatus.is_saving);
    let updatedSelected = { ...selected };
    updatedSelected.sf_hinhthucthanhtoan_id = selected.sf_hinhthucthanhtoan_id;
    updatedSelected.hinh_thuc_thanh_toan = selected.hinh_thuc_thanh_toan;

    if (selectedTienThua.so_tien > 0) {
      updatedSelected.so_tien = selectedTienThua.so_tien;
    }

    const res = await phanBoPhiApi.save_changes(updatedSelected);

    if (res.is_success) {
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: "Cập nhật thành công", type: "success" });
      setSelected(null);
      setSelectedTienThua({ so_tien: 0 });
      setSelectedHinhThuc(null);
      handleReloadPhanBoPhi();
      handleReloadTienThuaHocSinh();
      if (props.onPhanBoPhiChanged) {
        props.onPhanBoPhiChanged();
      }
    } else {
      setStatus(PageBaseStatus.is_completed);
      showNotify({
        message: res.message || "Cập nhật thất bại",
        type: "warning",
      });
    }
  };

  const handleClear = async () => {
    if (selected && selected.sf_hinhthucthanhtoan_id) {
      // Kiểm tra xem có data trong sf_phanbophis không
      const existingData = sf_phanbophis.find(
        (x: any) => x.sf_hinhthucthanhtoan_id === selected.sf_hinhthucthanhtoan_id
      );

      if (existingData && existingData.id) {
        // Nếu có data thì xóa record
        await handleBtnDeleteClicked(existingData.id);
      } else {
        // Nếu không có data thì chỉ clear form
        setSelected(null);
        setSelectedTienThua({ so_tien: 0 });
        setSelectedHinhThuc(null);
      }
    } else {
      // Nếu không có sf_hinhthucthanhtoan_id thì chỉ clear form
      setSelected(null);
      setSelectedTienThua({ so_tien: 0 });
      setSelectedHinhThuc(null);
    }
  };

  const handleBtnEditClicked = (data: any) => {
    // Tìm hình thức thanh toán tương ứng
    const hinhThuc = sf_hinhthucthanhtoans.find(
      (x: any) => x.id === data.sf_hinhthucthanhtoan_id
    );

    if (hinhThuc) {
      // Gọi onHinhThucClick để selected hình thức đó
      onHinhThucClick({ ...hinhThuc, phanBoInfo: data });
    } else {
      // Fallback: set selected trực tiếp nếu không tìm thấy hình thức
      setSelected(data);
      setSelectedTienThua({ so_tien: data.so_tien || 0 });
    }
  };

  const handleBtnDeleteClicked = async (id: number) => {
    const res = await phanBoPhiApi.delete(id);
    if (res.is_success) {
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: "Xóa thành công", type: "success" });
      setSelected(null);
      setSelectedTienThua({ so_tien: 0 });
      setSelectedHinhThuc(null);
      handleReloadPhanBoPhi();
      handleReloadTienThuaHocSinh();
      if (props.onPhanBoPhiChanged) {
        props.onPhanBoPhiChanged();
      }
    } else {
      setStatus(PageBaseStatus.is_completed);
      showNotify({ message: res.message || "Xóa thất bại", type: "warning" });
    }
  };

  const getHinhThucInfo = (hinhThucId: number) => {
    return sf_phanbophis?.find(
      (x: any) => x.sf_hinhthucthanhtoan_id === hinhThucId
    );
  };

  const columns: IColumn[] = [
    {
      caption: "#",
      id: "actions",
      width: 50,
      fixed: true,
      fixedPosition: "left",
      align: "center",
      cellRender: (data: any) => {
        return (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ActionMenu>
              <ActionMenu.Button
                icon={KebabHorizontalIcon}
                variant="invisible"
                sx={{ p: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                &nbsp;
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList>
                  <ActionList.Item
                    onSelect={() => handleBtnEditClicked(data)}
                  >
                    <ActionList.LeadingVisual>
                      <PencilIcon />
                    </ActionList.LeadingVisual>
                    Sửa
                  </ActionList.Item>
                  <ActionList.Item
                    variant="danger"
                    onSelect={() => handleBtnDeleteClicked(data.id)}
                  >
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Xóa
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </Box>
        );
      },
    },
    {
      caption: "Hình thức",
      dataField: "hinh_thuc_thanh_toan",
      width: 120,
      align: "left",
      fixed: true,
      fixedPosition: "left",
      cellRender: (data: any) => {
        return (
          <TextWithEllipse
            text={data.hinh_thuc_thanh_toan || ""}
            lineNumber={1}
          />
        );
      },
    },
    {
      caption: "Số tiền",
      dataField: "so_tien",
      width: 100,
      align: "right",
      cellRender: (data: any) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", textAlign: "right" }}>
            <Box sx={{ textAlign: "right" }}>
              {(data.so_tien || 0).toLocaleString()}
            </Box>
            {data.ngay_thu && (
              <Box sx={{ fontSize: "0.9em", color: "#6c757d", opacity: 0.8, textAlign: "right" }}>
                {moment(data.ngay_thu).format("DD/MM/YYYY")}
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      caption: "Ghi chú",
      dataField: "ghi_chu",
      width: 200,
      align: "left",
      cellRender: (data: any) => {
        return (
          <TextWithEllipse
            text={data.ghi_chu || ""}
            lineNumber={1}
          />
        );
      },
    },

  ];

  return (
    <Box sx={{ p: 1 }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          p: 1,
          bg: "canvas.default",
          boxShadow: "shadow.small",
        }}
      >
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              borderRadius: 1,
              p: 1,
              mb: 1,
              backgroundColor: "canvas.subtle",
              border: "1px solid",
              borderColor: "border.subtle",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Số tiền thừa:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "success.fg", fontSize: 13 }}
              >
                {soTienThuaHienThi.toLocaleString()}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng định mức:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienDinhMuc.toLocaleString()}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng miễn giảm:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienMienGiam.toLocaleString()}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Tổng phải nộp:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "fg.default", fontSize: 13 }}
              >
                {tongTienPhaiNop.toLocaleString()}
              </Box>
            </Box>
            {sf_phanbophis && sf_phanbophis.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <Box
                  as="span"
                  sx={{ fontWeight: "medium", color: "fg.default" }}
                >
                  Đã nộp:
                </Box>
                <Box
                  as="span"
                  sx={{ fontWeight: "700", color: "accent.fg", fontSize: 13 }}
                >
                  {sf_phanbophis
                    .map((x: any) => x.so_tien)
                    .reduce((a: number, b: number) => a + b, 0)
                    .toLocaleString()}
                </Box>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: 12,
                lineHeight: 1.5,
              }}
            >
              <Box as="span" sx={{ fontWeight: "medium", color: "fg.default" }}>
                Còn lại:
              </Box>
              <Box
                as="span"
                sx={{ fontWeight: "700", color: "danger.fg", fontSize: 13 }}
              >
                {tongTienConLai > 0 ? tongTienConLai.toLocaleString() : "0"}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          as="hr"
          sx={{
            mt: 1,
            border: "none",
            borderColor: "border.default",
          }}
        />
        <Box
          as="h3"
          sx={{
            fontSize: 1,
            fontWeight: "bold",
            mt: 1,
            borderBottom: "1px solid",
            borderColor: "border.subtle",
            pb: 0.5,
            textAlign: "center",
            color: "fg.default",
          }}
        >
          Hình thức thanh toán
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            maxHeight: "300px",
            overflowY: "auto",
            pr: 0.5,
          }}
        >
          {sf_hinhthucthanhtoans.map((x: any) => {
            const phanBoInfo = getHinhThucInfo(x.id);
            const isSelected =
              selectedHinhThuc?.id === x.id ||
              selectedHinhThuc?.sf_hinhthucthanhtoan_id === x.id;
            return (
              <Box
                key={x.id}
                onClick={() => onHinhThucClick({ ...x, phanBoInfo })}
                className={`hinh-thuc-item ${isSelected ? "selected" : ""}`}
                sx={{
                  flex: "1 1 calc(33.333% - 4px)",
                  minWidth: "calc(33.333% - 4px)",
                  boxSizing: "border-box",
                }}
              >
                {x.img ? (
                  <Box
                    as="img"
                    src={x.img}
                    alt="Hình thức"
                    sx={{
                      width: "auto",
                      height: 18,
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      mr: 1.5,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 0,
                      bg: "canvas.subtle",
                      border: "1px solid",
                      borderColor: "border.default",
                      borderRadius: 1,
                    }}
                  >
                    💳
                  </Box>
                )}
                <Box
                  as="span"
                  sx={{
                    flex: 1,
                    fontSize: 0,
                    fontWeight: "medium",
                    color: "fg.default",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflow: "visible",
                    textOverflow: "clip",
                    textAlign: "left",
                  }}
                >
                  {x.hinh_thuc_thanh_toan}
                </Box>
              </Box>
            );
          })}
        </Box>
        {selected && selected.hinh_thuc_thanh_toan && (
          <>
            <Box
              as="hr"
              sx={{
                my: 1,
                border: "none",
                borderTop: "1px solid",
                borderColor: "border.default",
              }}
            />
            {selected.hinh_thuc_thanh_toan
              ?.toLowerCase()
              .includes("trả trước") ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: 0.5,
                    }}
                  >
                    <Box
                      as="label"
                      sx={{
                        margin: 0,
                        fontWeight: "medium",
                        fontSize: 0,
                        color: "fg.default",
                      }}
                    >
                      Số tiền:
                    </Box>
                    <NumberInput
                      min={0}
                      max={
                        tong_tien_thua - tong_tien_tra_truoc <= 0
                          ? 0
                          : tong_tien_thua - tong_tien_tra_truoc
                      }
                      value={selectedTienThua.so_tien || 0}
                      onChange={(e) => {
                        setSelectedTienThua({
                          ...selectedTienThua,
                          so_tien: Number(e.target.value),
                        });
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: 0.5,
                    }}
                  >
                    <Box
                      as="label"
                      sx={{
                        margin: 0,
                        fontWeight: "medium",
                        fontSize: 0,
                        color: "fg.default",
                      }}
                    >
                      Tiền thừa:
                    </Box>
                    <NumberInput
                      min={0}
                      value={
                        tong_tien_thua_temp - tong_tien_tra_truoc < 0
                          ? 0
                          : tong_tien_thua_temp - tong_tien_tra_truoc
                      }
                      readOnly
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Box
                    as="label"
                    sx={{
                      margin: 0,
                      fontWeight: "medium",
                      fontSize: 0,
                      color: "fg.default",
                    }}
                  >
                    Ngày thu:
                  </Box>
                  <input
                    type="date"
                    className="text-box"
                    value={
                      selected.ngay_thu
                        ? moment(selected.ngay_thu).format("YYYY-MM-DD")
                        : ""
                    }
                    onChange={(e) => {
                      const fullDate = moment(e.target.value).format(
                        "YYYY-MM-DD HH:mm:ss"
                      );
                      setSelected({ ...selected, ngay_thu: fullDate });
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Box
                    as="label"
                    sx={{
                      margin: 0,
                      fontWeight: "medium",
                      fontSize: 0,
                      color: "fg.default",
                    }}
                  >
                    Ghi chú:
                  </Box>
                  <TextInput
                    block
                    value={selected.ghi_chu || ""}
                    onChange={(e) => {
                      setSelected({ ...selected, ghi_chu: e.target.value });
                    }}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: 0.5,
                    }}
                  >
                    <Box
                      as="label"
                      sx={{
                        margin: 0,
                        fontWeight: "medium",
                        fontSize: 0,
                        color: "fg.default",
                      }}
                    >
                      Số tiền:
                    </Box>
                    <NumberInput
                      min={0}
                      value={selected.so_tien <= 0 ? 0 : selected.so_tien}
                      onChange={(e) => {
                        setSelected({
                          ...selected,
                          so_tien: Number(e.target.value),
                        });
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      gap: 0.5,
                    }}
                  >
                    <Box
                      as="label"
                      sx={{
                        margin: 0,
                        fontWeight: "medium",
                        fontSize: 0,
                        color: "fg.default",
                      }}
                    >
                      Ngày thu:
                    </Box>
                    <input
                      type="date"
                      className="text-box"
                      value={
                        selected.ngay_thu
                          ? moment(selected.ngay_thu).format("YYYY-MM-DD")
                          : ""
                      }
                      onChange={(e) => {
                        const fullDate = moment(e.target.value).format(
                          "YYYY-MM-DD HH:mm:ss"
                        );
                        setSelected({ ...selected, ngay_thu: fullDate });
                      }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Box
                    as="label"
                    sx={{
                      margin: 0,
                      fontWeight: "medium",
                      fontSize: 0,
                      color: "fg.default",
                    }}
                  >
                    Ghi chú:
                  </Box>
                  <TextInput
                    block
                    value={selected.ghi_chu || ""}
                    onChange={(e) => {
                      setSelected({ ...selected, ghi_chu: e.target.value });
                    }}
                  />
                </Box>
              </>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                py: 1,
                mt: 0.5,
              }}
            >
              {(() => {
                const existingData = selected && selected.sf_hinhthucthanhtoan_id
                  ? sf_phanbophis.find(
                    (x: any) => x.sf_hinhthucthanhtoan_id === selected.sf_hinhthucthanhtoan_id
                  )
                  : null;
                const hasExistingData = !!existingData;
                const hasBienLai = props.sf_bienlaithu !== undefined && props.sf_bienlaithu.length > 0;

                const clearIcon = hasExistingData ? TrashIcon : XIcon;

                return (
                  <>
                    <Button
                      variant={hasExistingData ? "danger" : "default"}
                      leadingVisual={clearIcon}
                      text={hasExistingData ? "Xóa" : "Đóng"}
                      disabled={!selected || hasBienLai}
                      isLoading={status === PageBaseStatus.is_saving}
                      onClick={handleClear}
                    />
                    <Button
                      variant="primary"
                      leadingVisual={CheckIcon}
                      text={hasExistingData ? "Cập nhật" : "Thêm mới"}
                      disabled={
                        !selected ||
                        hasBienLai ||
                        (selected.hinh_thuc_thanh_toan
                          ?.toLowerCase()
                          .includes("trả trước")
                          ? selectedTienThua?.so_tien === 0 ||
                          !selectedTienThua?.so_tien
                          : selected.so_tien === 0 || !selected.so_tien)
                      }
                      isLoading={status === PageBaseStatus.is_saving}
                      onClick={handleSaveChanges}
                    />
                  </>
                );
              })()}
            </Box>
          </>
        )}
        <Box sx={{ mt: 1 }}>
          <DataTable
            data={sf_phanbophis}
            columns={columns}
            paging={{ enable: false }}
            filterRow={{ enable: false }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ThuPhiThongTinTongHop;
